import BackgroundImage from '@assets/media/login_background.png';
import { ViewContainer } from 'App.styles';
import {
  getLastWatchedEpgs,
  getLiveCategories,
  getLiveChannels,
  getLogos,
  getMostWatchedEpgs,
  getRecordedCategories,
  getTranslations,
  getUserDetails,
  retrievePrismicRadioImages,
  signIn
} from 'common/actions';
import DebugView from 'common/components/debug-view/DebugView';
import LoadingIndicator, { LoadingState } from 'common/components/loading-indicator';
import { TrialModal } from 'common/components/trial';
import { checkNeedsTrialReminderPopup } from 'common/components/trial/utils';
import { ROUTE_ERROR_LOADING_DATA, ROUTE_HOME, ROUTE_TRIAL_WELCOME } from 'common/config/constants';
import variables from 'common/config/variables';
import { usePopup } from 'common/contexts/popup';
import { LocalStorageTrialObject } from 'common/interfaces';
import {
  getTokenObject,
  getTrialObject,
  hasCredentialsObjectInMemory,
  hasTokenInMemory
} from 'common/services/helpers';
import { getRemoteKeyName } from 'common/utils';
import { usePageNavigation, useRequiredData } from 'common/utils/hooks';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

const Container = styled(ViewContainer)`
  background: url(${BackgroundImage});
  background-size: cover;
`;

function Launcher(props: any) {
  const navigate = usePageNavigation();
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const { get: fetchData, loadingRequiredData } = useRequiredData();
  const { openPopup } = usePopup();
  const [debug, setDebug] = useState(false);

  useEffect(() => {
    if (hasTokenInMemory() || hasCredentialsObjectInMemory()) {
      fetchData().then((response) => {
        // onError
        if (!response) {
          navigate(ROUTE_ERROR_LOADING_DATA, true, true);
        }
      });
    }
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const tokenObject = getTokenObject();

    // Make sure we have token
    if (!loadingRequiredData && !isEmpty(tokenObject) && !props.shouldWaitForPlayer) {
      timeout = setTimeout(() => {
        setLoadingState('done');
      }, variables.LOADING_PRELOAD_TIMEOUT);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [props.shouldWaitForPlayer, loadingRequiredData]);

  // Handle key holding to open debug screen
  useEffect(() => {
    let holdTimeout: NodeJS.Timeout | undefined = undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (getRemoteKeyName(e.keyCode) === 'ARROW_UP') {
        if (!holdTimeout) {
          holdTimeout = setTimeout(() => {
            setDebug((prev) => !prev);
          }, variables.HOLD_TIMEOUT);
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (getRemoteKeyName(e.keyCode) === 'ARROW_UP') {
        clearTimeout(holdTimeout);
        holdTimeout = undefined;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      clearTimeout(holdTimeout);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const showTrialWelcomeScreen = useMemo(() => {
    const trialObject = getTrialObject() as LocalStorageTrialObject;

    // Check if the account is a trial account
    // Check if the previous trial account was the same
    return !isEmpty(props.userDetails?.trial) && trialObject?.email !== props.userDetails?.email;
  }, [props.userDetails]);

  const handlePopupReminderForTrial = useCallback(() => {
    // Check trial expiration
    // Show popup when the next screen is not the welcome screen
    if (!showTrialWelcomeScreen) {
      checkNeedsTrialReminderPopup(props.userDetails) &&
        openPopup({
          id: 'TRIAL-MODAL',
          returnToLastFocused: false,
          type: 'primary',
          content: <TrialModal />
        });
    }
  }, [props.userDetails, showTrialWelcomeScreen]);

  const onLoadingFinish = () => {
    handlePopupReminderForTrial();
    navigate(showTrialWelcomeScreen ? ROUTE_TRIAL_WELCOME : ROUTE_HOME, true, true);
  };

  return (
    <Container data-loading-state={loadingState}>
      <LoadingIndicator state={loadingState} onFinish={onLoadingFinish} />
      {debug && <DebugView />}
    </Container>
  );
}

const mapStateToProps = ({ userDetails, videoPlayer }: any) => ({
  userDetails,
  shouldWaitForPlayer: variables.LOADING_WAITS_FOR_PLAYER ? !videoPlayer.isPlaying : false
});
export default React.memo(
  connect(mapStateToProps, {
    signIn,
    getLiveChannels,
    getLastWatchedEpgs,
    getLiveCategories,
    getLogos,
    getTranslations,
    getRecordedCategories,
    getMostWatchedEpgs,
    getUserDetails,
    retrievePrismicRadioImages
  })(Launcher)
);
